import React from 'react';

// Small building blocks shared by the insurance screens. Inline styles pinned to
// the brand tokens, matching the rest of the app.

export const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 14, fontFamily: 'inherit',
  border: '1px solid var(--border-color)', borderRadius: 6, background: 'var(--white)',
};
export const selectStyle = { ...inputStyle, height: 36 };
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-strong)', margin: '10px 0 4px' };
const TONE_COLORS = { good: 'var(--success-color)', warn: '#B7791F', bad: 'var(--error-color)', neutral: 'var(--muted-color)', info: 'var(--primary-color)' };

// Text color + translucent tint for a tone. The tint must be built with
// color-mix(): appending a hex alpha to a var() token ("var(--x)20") is invalid
// CSS and the browser silently drops the whole background.
export function toneColors(tone, tintPercent = 12) {
  const color = TONE_COLORS[tone] || TONE_COLORS.neutral;
  return { color, background: `color-mix(in srgb, ${color} ${tintPercent}%, transparent)` };
}

export function Field({ label, error, hint, children, style }) {
  return (
    <div style={{ flex: 1, minWidth: 160, ...style }}>
      {label ? <label style={labelStyle}>{label}</label> : null}
      {children}
      {hint && !error ? <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{hint}</div> : null}
      {error ? <div role="alert" style={{ fontSize: 12, color: 'var(--error-color)', marginTop: 3 }}>{error}</div> : null}
    </div>
  );
}

export function Row({ children }) {
  return <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>{children}</div>;
}

// `wrap` lets long labels (payer names) break onto a second line; inside tables
// keep the default single line.
export function StatusBadge({ tone = 'neutral', wrap = false, children }) {
  const { color, background } = toneColors(tone, 14);
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background, color, whiteSpace: wrap ? 'normal' : 'nowrap', lineHeight: 1.5 }}>
      {children}
    </span>
  );
}

export function Notice({ tone = 'info', children }) {
  const { color, background } = toneColors(tone === 'info' ? 'info' : tone, 9);
  return (
    <div role={tone === 'bad' ? 'alert' : 'status'} style={{ background, borderLeft: `4px solid ${color}`, color: 'var(--text-strong)', padding: '8px 12px', borderRadius: 6, fontSize: 13, margin: '10px 0' }}>
      {children}
    </div>
  );
}

// Tab button style shared by the insurance pages (same look as the client profile tabs).
export const tabStyle = (active) => ({
  padding: '8px 16px', marginRight: 6, border: 'none', borderBottom: active ? '3px solid var(--primary-color)' : '3px solid transparent',
  background: 'transparent', color: active ? 'var(--primary-color)' : 'var(--text-muted)', fontWeight: 700, fontSize: 14, cursor: 'pointer',
});

// Turns an Axios error into something a form can show: field errors from a 400
// ({ errors: { field: message } }), or one message for everything else.
export function apiErrors(err) {
  const data = err && err.response && err.response.data;
  if (data && data.errors && typeof data.errors === 'object') {
    return { fieldErrors: data.errors, message: 'Please fix the highlighted fields.' };
  }
  // 400s carry { message }; the auth middleware's 403s carry { error }.
  return { fieldErrors: {}, message: (data && (data.message || data.error)) || 'Something went wrong. Please try again.' };
}
